<template>
  <div class="column-configurator">
    <el-dialog
      title="表格列配置"
      :visible.sync="dialogVisible"
      width="800px"
      @close="handleCancel"
    >
      <el-row :gutter="20">
        <!-- 左侧列选择区域 -->
        <el-col :span="12">
          <div class="config-section">
            <div class="section-header">
              <span>可选列（{{ totalColumnsCount }}）</span>
              <el-input
                v-model="searchQuery"
                placeholder="搜索列..."
                size="small"
                class="search-input"
                clearable
              />
            </div>
            <div class="checkbox-group">
              <el-checkbox
                v-model="checkAll"
                :indeterminate="isIndeterminate"
                @change="handleCheckAllChange"
              >
                全选
              </el-checkbox>
              <el-checkbox-group
                v-model="selectedColumns"
                @change="handleSelectedChange"
              >
                <draggable
                  v-model="filteredColumns"
                  group="columns"
                  @start="drag = true"
                  @end="drag = false"
                >
                  <transition-group>
                    <div
                      v-for="col in filteredColumns"
                      :key="col.field"
                      class="column-item"
                    >
                      <el-checkbox :label="col.field">
                        {{ col.title }}
                      </el-checkbox>
                    </div>
                  </transition-group>
                </draggable>
              </el-checkbox-group>
            </div>
          </div>
        </el-col>

        <!-- 右侧预览区域 -->
        <el-col :span="12">
          <div class="config-section">
            <div class="section-header">
              <span>已选列（{{ selectedColumns.length }}）</span>
              <el-tag type="info" size="mini">拖拽调整顺序</el-tag>
            </div>
            <draggable
              v-model="selectedColumns"
              group="columns"
              @start="drag = true"
              @end="handleOrderChange"
            >
              <transition-group>
                <div
                  v-for="col in orderedColumns"
                  :key="col.field"
                  class="column-item"
                >
                  <el-tag
                    type="info"
                    size="small"
                    closable
                    @close="handleRemoveColumn(col.field)"
                  >
                    {{ col.title }}
                  </el-tag>
                </div>
              </transition-group>
            </draggable>
          </div>
        </el-col>
      </el-row>

      <div slot="footer" class="dialog-footer">
        <el-button @click="handleCancel">取 消</el-button>
        <el-button type="primary" @click="handleConfirm">确 定</el-button>
      </div>
    </el-dialog>
  </div>
</template>
  
  <script>
import draggable from "vuedraggable";

export default {
  name: "ColumnConfigurator",
  components: { draggable },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    columns: {
      type: Array,
      required: true,
      validator: (value) => value.every((col) => col.field && col.title),
    },
  },
  data() {
    return {
      dialogVisible: this.visible,
      searchQuery: "",
      localColumns: [],
      selectedColumns: [],
      checkAll: false,
      isIndeterminate: false,
      drag: false,
    };
  },
  computed: {
    // 过滤后的列（用于搜索）
    filteredColumns() {
      const query = this.searchQuery.toLowerCase();
      return this.localColumns.filter(
        (col) =>
          col.title.toLowerCase().includes(query) ||
          col.field.toLowerCase().includes(query)
      );
    },
    // 排序后的列（按选择顺序）
    orderedColumns() {
      return this.selectedColumns
        .map((field) => this.localColumns.find((col) => col.field === field))
        .filter((col) => col);
    },
    totalColumnsCount() {
      return this.localColumns.length;
    },
  },
  watch: {
    visible(val) {
      this.dialogVisible = val;
      if (val) this.initializeData();
    },
    selectedColumns(val) {
      this.isIndeterminate =
        val.length > 0 && val.length < this.localColumns.length;
      this.checkAll = val.length === this.localColumns.length;
    },
  },
  methods: {
    initializeData() {
      // 深拷贝原始数据
      this.localColumns = JSON.parse(JSON.stringify(this.columns));
      this.selectedColumns = this.columns
        .filter((col) => col.checked !== false)
        .map((col) => col.field);
    },
    handleCheckAllChange(val) {
      this.selectedColumns = val
        ? this.localColumns.map((col) => col.field)
        : [];
    },
    handleSelectedChange(value) {
      this.checkAll = value.length === this.localColumns.length;
      this.isIndeterminate =
        value.length > 0 && value.length < this.localColumns.length;
    },
    handleRemoveColumn(field) {
      this.selectedColumns = this.selectedColumns.filter((p) => p !== field);
    },
    handleOrderChange() {
      // 同步更新localColumns的顺序
      const ordered = this.selectedColumns
        .map((field) => this.localColumns.find((col) => col.field === field))
        .filter((col) => col);
      this.localColumns = [
        ...ordered,
        ...this.localColumns.filter(
          (col) => !this.selectedColumns.includes(col.field)
        ),
      ];
    },
    handleConfirm() {
      const result = this.localColumns.map((col) => ({
        ...col,
        checked: this.selectedColumns.includes(col.field),
      }));
      // this.$emit("update:columns", result);
      this.$emit("confirm", result);
      this.dialogVisible = false;
    },
    handleCancel() {
      this.initializeData();
      this.dialogVisible = false;
      this.$emit("cancel");
    },
  },
};
</script>
  
  <style scoped>
.config-section {
  border: 1px solid #ebeef5;
  border-radius: 4px;
  padding: 10px;
  height: 400px;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.search-input {
  width: 150px;
}

.checkbox-group {
  flex: 1;
  overflow-y: auto;
}

.column-item {
  padding: 8px;
  margin: 4px 0;
  background: #f5f7fa;
  border-radius: 4px;
  cursor: move;
  transition: all 0.3s;
}

.column-item:hover {
  background: #ebeff5;
  transform: translateX(5px);
}

.column-item.sortable-chosen {
  background: #ebeff5;
}

.column-item.sortable-ghost {
  opacity: 0.5;
}

.dialog-footer {
  text-align: right;
}
>>> .el-tag.el-tag--info {
  color: #000;
}
</style>